/**
 * Formats an ISO date string into a human-readable date string in the user's local timezone.
 *
 * @param dateString - The date string to format. Can be:
 *   - Full ISO string with timezone: '2025-09-06T01:31:08Z' (UTC)
 *   - Full ISO string without timezone: '2025-09-06T01:31:08' (treated as local time)
 *   - Date-only string: '2025-09-06' (treated as UTC midnight - may cause timezone issues)
 *
 * @returns A formatted date string in the format "Month Day, Year" (e.g., "September 5, 2025")
 *          Returns empty string if the input is invalid or empty.
 *
 * @example
 * // Full ISO string with timezone (recommended)
 * formatDateFromISO('2025-09-06T01:31:08Z') // "September 5, 2025" (in Pacific Time)
 *
 * @example
 * // Date-only string (may cause timezone issues)
 * formatDateFromISO('2023-10-01') // "September 30, 2023" (in Pacific Time) or "October 1, 2023" (in UTC)
 *
 * @example
 * // Invalid input
 * formatDateFromISO('invalid-date') // ""
 * formatDateFromISO('') // ""
 *
 * @note
 * - The function uses the user's local timezone for display
 * - Date-only strings (YYYY-MM-DD) are treated as UTC midnight, which may cause
 *   the displayed date to be one day earlier in timezones behind UTC
 * - For consistent behavior across timezones, use full ISO strings with timezone
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString} toLocaleDateString documentation
 */
export const formatDateFromISO = (dateString: string): string => {
  const date = new Date(dateString);

  if (!date.getTime()) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatToISODate = (date: Date = new Date()): string => date.toISOString().split('.')[0];

export const getCurrentDateString = (date: Date = new Date()): string => date.toISOString().split('T')[0];
